import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection, disconnectSocket, refreshSocketToken } from "../../services/socket";
import moment from "moment";

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState(null);
  
  // Ref para manter referência dos listeners ativos
  const listenersRef = useRef(new Set());

  const refreshRequestRef = useRef(null);
  useEffect(() => {
    const requestId = api.interceptors.request.use((config) => {
      const storedToken = localStorage.getItem("token");
      if (storedToken) {
        try { config.headers.Authorization = `Bearer ${JSON.parse(storedToken)}`; }
        catch (_) { localStorage.removeItem("token"); }
      }
      return config;
    }, (error) => Promise.reject(error));
    const responseId = api.interceptors.response.use((response) => response, async (error) => {
      const originalRequest = error.config;
      if (["ERR_ACCESS_BLOCKED", "ERR_PORTAL_MISMATCH"].includes(error?.response?.data?.error)) {
        localStorage.removeItem("token");
        delete api.defaults.headers.Authorization;
        setIsAuth(false);
        setUser({});
        disconnectSocket?.();
        return Promise.reject(error);
      }
      const isAuthRequest = /\/auth\/(refresh_token|login|logout)/.test(originalRequest?.url || "");
      if (error?.response?.status === 403 && originalRequest && !originalRequest._retry && !isAuthRequest) {
        originalRequest._retry = true;
        try {
          if (!refreshRequestRef.current) {
            refreshRequestRef.current = api.post("/auth/refresh_token").finally(() => { refreshRequestRef.current = null; });
          }
          const { data } = await refreshRequestRef.current;
          localStorage.setItem("token", JSON.stringify(data.token));
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          refreshSocketToken();
          originalRequest.headers.Authorization = `Bearer ${data.token}`;
          return api(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem("token");
          delete api.defaults.headers.Authorization;
          setIsAuth(false);
          return Promise.reject(refreshError);
        }
      }
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        delete api.defaults.headers.Authorization;
        setIsAuth(false);
      }
      return Promise.reject(error);
    });
    return () => {
      api.interceptors.request.eject(requestId);
      api.interceptors.response.eject(responseId);
    };
  }, []);

  // Effect para inicialização do token
  useEffect(() => {
    const token = localStorage.getItem("token");
    (async () => {
      if (token) {
        try {
          const { data } = await api.post("/auth/refresh_token");
          localStorage.setItem("token", JSON.stringify(data.token));
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          setIsAuth(true);
          setUser(data.user || data);
        } catch (err) {
          toastError(err);
        }
      }
      setLoading(false);
    })();
  }, []);

  const userId = user.id;
  const companyId = user.companyId;
  useEffect(() => {
    if (!userId || !companyId) return;
    const instance = socketConnection({ user: { id: userId, companyId } });
    if (!instance?.on) return;
    setSocket(instance);
    const eventName = `company-${companyId}-user`;
    const onUpdate = (data) => {
      if (data.action === "update" && data.user.id === userId) setUser((previous) => ({ ...previous, ...data.user }));
    };
    instance.on(eventName, onUpdate);
    const onBlocked = () => {
      localStorage.removeItem("token");
      delete api.defaults.headers.Authorization;
      instance.disconnect?.();
      setIsAuth(false);
      setUser({});
      toastError("Acesso suspenso. Entre em contato com seu administrador.");
    };
    instance.on("whitelabel:blocked", onBlocked);
    const removeListener = () => { instance.off?.(eventName, onUpdate); instance.off?.("whitelabel:blocked", onBlocked); };
    listenersRef.current.add(removeListener);
    const listeners = listenersRef.current;
    return () => { removeListener(); listeners.delete(removeListener); };
  }, [userId, companyId]);

  // Effect para buscar dados do usuário atual
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user || data);
      } catch (err) {
        console.log("Erro ao buscar usuário atual:", err);
      }
    };
    
    if (isAuth) {
      fetchCurrentUser();
    }
  }, [isAuth]);

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);
      const {
        user: { company },
      } = data;

      // Lógica de configurações da empresa (mantém como estava)
      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        const signEnable = setting?.value === "enabled";

        if (setting && setting.value === "enabled") {
          localStorage.setItem("sendSignMessage", signEnable);
        }
      }
      
      if (data.user.profileImage) localStorage.setItem("profileImage", data.user.profileImage);
      else localStorage.removeItem("profileImage");

      moment.locale("pt-br");
      let dueDate;
      if (data.user.company.id === 1) {
        dueDate = "2999-12-31T00:00:00.000Z";
      } else {
        dueDate = data.user.company.dueDate;
      }
      
      const vencimento = moment(dueDate).format("DD/MM/yyyy");

      // Comparar apenas as datas (sem horas) para permitir acesso até 23h59 do dia do vencimento
      const hojeInicio = moment().startOf('day');
      const vencimentoInicio = moment(dueDate).startOf('day');
      
      var diff = vencimentoInicio.diff(hojeInicio, 'days');
      var before = hojeInicio.isSameOrBefore(vencimentoInicio, 'day');
      var dias = diff;

      if (before === true) {
        localStorage.setItem("token", JSON.stringify(data.token));
        localStorage.setItem("companyDueDate", vencimento);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user || data);
        setIsAuth(true);
        toast.success(i18n.t("auth.toasts.success"));
        
        if (Math.round(dias) < 5) {
          toast.warn(
            `Sua assinatura vence em ${Math.round(dias)} ${
              Math.round(dias) === 1 ? "dia" : "dias"
            } `
          );
        }

        history.push("/tickets");
        setLoading(false);
      } else {
        localStorage.setItem("token", JSON.stringify(data.token));
        setUser(data.user);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setIsAuth(true);
        toastError(`Opss! Sua assinatura venceu ${vencimento}.
Entre em contato com o Suporte para mais informações! `);
        history.push("/financeiro-aberto");
        setLoading(false);
      }
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      // Limpar socket antes do logout
      if (socket) {
        listenersRef.current.forEach((removeListener) => removeListener());
        listenersRef.current.clear();
        
        if (typeof socket.disconnect === 'function') {
          socket.disconnect();
        }
      }

      await api.delete("/auth/logout");
      setIsAuth(false);
      setUser({});
      setSocket(null);
      localStorage.removeItem("token");
      localStorage.removeItem("cshow");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      console.log(data);
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    socket,
  };
};

export default useAuth;
